import React from 'react';
import { Box, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SupportAgentRounded from '@mui/icons-material/SupportAgentRounded';
import WhatsApp from '@mui/icons-material/WhatsApp';
import MarkEmailReadRounded from '@mui/icons-material/MarkEmailReadRounded';
import FactCheckRounded from '@mui/icons-material/FactCheckRounded';
import SummarizeRounded from '@mui/icons-material/SummarizeRounded';
import RequestQuoteRounded from '@mui/icons-material/RequestQuoteRounded';
import SmartToyOutlined from '@mui/icons-material/SmartToyOutlined';
import { EmptyState, EntityTile, PageHeader, Panel } from '../../ui-kit';
import { ConfirmDialog } from '../../components/dialog';
import { useCan } from '../../ui/PermissionsProvider';
import {
  deleteTenantAgent, fetchTenantAgents, updateTenantAgent, type TenantAgent,
} from '../../services/tenantAgentsService';
import PlaybooksListPage from '../playbooks/PlaybooksListPage';
import { EditDetailsModal, tileMenu } from './tileMenu';

// Every tile on the page shares one grid: three across on a wide screen.
const GRID = { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' };

/**
 * A policy's journey through an agency, in order. Each agent works at one
 * stage (its kind says which), and the page groups them so. A stage with no
 * agent in this workspace is not drawn.
 *
 * Under each stage's name is what an agency gains from AI agents there,
 * said for the stage and not for one agent, so it holds whichever agents a
 * workspace has in it and whatever is added later.
 */
const STAGES: { key: string; title: string; wins: string }[] = [
  { key: 'intake', title: 'Intake', wins: 'Improve contact rate and conversions' },
  { key: 'quote', title: 'Quote', wins: 'Quote faster and compare more carriers for every client' },
  { key: 'bind_issue', title: 'Bind and issue', wins: 'Shorten time to issue and catch errors before delivery' },
  { key: 'service', title: 'Service', wins: 'Answer clients faster and free up the service team' },
];

// What the saved analyses' group gains, said the same way.
const INSIGHTS_WINS = 'See what is working without manual reporting';

/** "1 agent", "2 agents", "3 saved analyses". */
function countOf(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

const ICONS: Record<string, React.ElementType> = {
  calls: SupportAgentRounded,
  inbox: MarkEmailReadRounded,
  whatsapp: WhatsApp,
  check: FactCheckRounded,
  report: SummarizeRounded,
  quote: RequestQuoteRounded,
};

/**
 * /agents: every agent the workspace has, grouped by where it works in a
 * policy's journey: intake, quote, bind and issue, service.
 *
 * Each group is a panel that opens to its agents, and all start folded, so
 * the page opens as the shape of the agency's work, with each group saying
 * what the agency gains there and how many agents it has. Saved analyses (playbooks) come last, under Insights, as
 * tiles of the same kind with their Run now and Schedule buttons.
 *
 * Agents — ROCCO and the rest — are provisioned from the backend, like manual
 * dashboards; an admin can rename, re-describe or delete one from its ⋯ menu.
 * A workspace with no provisioned agents sees exactly the Playbooks page it
 * always had.
 */
export default function AgentsListPage() {
  const [agents, setAgents] = React.useState<TenantAgent[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    try {
      setAgents(await fetchTenantAgents());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load agents');
      setAgents([]);
    }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  if (agents === null) {
    // Tiles in outline while the list loads, so the page has its shape at once.
    return (
      <Box>
        <PageHeader title="Agents" description="Each one does a job end to end, on your own systems." />
        <Box sx={{ mt: 2, display: 'grid', gap: 2, gridTemplateColumns: GRID }}>
          {[0, 1, 2].map((i) => <EntityTile key={i} title="" loading />)}
        </Box>
      </Box>
    );
  }
  // No agents here: the page is the Playbooks page, as it always was.
  if (agents.length === 0) return <PlaybooksListPage />;

  return <AgentTiles agents={agents} error={error} onChanged={load} />;
}

function AgentTiles({
  agents, error, onChanged,
}: { agents: TenantAgent[]; error: string | null; onChanged: () => void }) {
  const navigate = useNavigate();
  const canManage = useCan('manage_workspace');
  const [editing, setEditing] = React.useState<TenantAgent | null>(null);
  const [deleting, setDeleting] = React.useState<TenantAgent | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [problem, setProblem] = React.useState<string | null>(null);

  const remove = async () => {
    if (!deleting) return;
    setBusy(true);
    setProblem(null);
    try {
      await deleteTenantAgent(deleting.id);
      onChanged();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not delete the agent');
    } finally {
      setDeleting(null);
      setBusy(false);
    }
  };

  const tile = (agent: TenantAgent) => {
    const Icon = ICONS[agent.icon || ''] || SmartToyOutlined;
    return (
      <EntityTile
        key={agent.id}
        title={agent.name}
        description={agent.description}
        icon={<Icon sx={{ fontSize: 20 }} />}
        status={agent.ready ? { tone: 'good', label: 'Live' } : { tone: 'neutral', label: 'Coming next' }}
        footnote={agent.ready ? null : 'Not switched on in this workspace yet'}
        onOpen={agent.ready ? () => navigate(`/agents/${agent.kind}/spotlight`) : undefined}
        actions={tileMenu({
          onOpen: agent.ready ? () => navigate(`/agents/${agent.kind}/spotlight`) : undefined,
          onEdit: canManage ? () => setEditing(agent) : undefined,
          onDelete: canManage ? () => setDeleting(agent) : undefined,
        })}
      />
    );
  };

  // Each stage with the agents this workspace has in it. An agent whose kind
  // names no stage we know is still shown, in a group of its own at the end.
  const known = new Set(STAGES.map((stage) => stage.key));
  const stray = agents.filter((agent) => !agent.stage || !known.has(agent.stage));
  const groups = [
    ...STAGES.map((stage) => ({ ...stage, agents: agents.filter((agent) => agent.stage === stage.key) })),
    { key: 'other', title: 'Other agents', wins: 'Hand routine work to an agent', agents: stray },
  ].filter((group) => group.agents.length > 0);

  return (
    <Box>
      <PageHeader
        title="Agents"
        description="Each one does a job end to end, on your own systems. Open a group to see its agents, then an agent to see how it is set up and what it has done."
      />
      {error || problem ? <EmptyState message={problem || error || ''} /> : null}
      <PlaybooksListPage
        renderAsTiles={(playbookTiles, playbooksLoading) => (
          <Stack spacing={2} sx={{ mt: 2 }}>
            {groups.map((group) => (
              <Panel
                key={group.key}
                title={group.title}
                subtitle={`${group.wins} · ${countOf(group.agents.length, 'agent', 'agents')}`}
                collapsible
                defaultCollapsed
              >
                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: GRID }}>
                  {group.agents.map(tile)}
                </Box>
              </Panel>
            ))}
            {playbookTiles.length || playbooksLoading ? (
              <Panel
                title="Insights"
                subtitle={`${INSIGHTS_WINS} · ${playbooksLoading && !playbookTiles.length ? 'loading'
                  : countOf(playbookTiles.length, 'saved analysis', 'saved analyses')}`}
                collapsible
                defaultCollapsed
              >
                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: GRID }}>
                  {playbookTiles}
                  {playbooksLoading ? <EntityTile title="" loading /> : null}
                </Box>
              </Panel>
            ) : null}
          </Stack>
        )}
      />

      {editing ? (
        <EditDetailsModal
          title={editing.name}
          name={editing.name}
          description={editing.description ?? ''}
          onClose={() => setEditing(null)}
          onSave={async (change) => {
            await updateTenantAgent(editing.id, change);
            setEditing(null);
            onChanged();
          }}
        />
      ) : null}

      <ConfirmDialog
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        title={`Delete ${deleting?.name ?? 'agent'}?`}
        description={deleting?.kind === 'rocco'
          ? `${deleting.name} stops calling leads the moment it is deleted, and disappears from Agents. `
            + 'A call already under way is allowed to finish. The calls it made and the callbacks it booked are kept.'
          : `${deleting?.name ?? 'It'} disappears from Agents. Anything it has already done is kept.`}
        tone="destructive"
        confirmLabel="Delete"
        requireTypeToConfirm={deleting?.kind === 'rocco' ? deleting.name : undefined}
        onConfirm={remove}
        busy={busy}
      />
    </Box>
  );
}
